import { useEffect, useState, useCallback } from "react";
import { db } from "../services/firebase";
import { ref, onValue, set, remove, update } from "firebase/database";

export default function useInterviewQuestions(domainId, categoryId, packId, interviewId) {
    
    const [questions, setQuestions] = useState([]);
    const [selectedQuestions, setSelectedQuestions] = useState({});
    const [loading, setLoading] = useState(true);


    useEffect(() => {
        if (!domainId) return;
        const qRef = ref(db, `Questions/CategoryQuestions/${domainId}/${categoryId}`);
        const unsubscribe = onValue(qRef, (snapshot) => {
            const data = snapshot.val();
            if (data) {
                const arr = Object.entries(data).map(([id, value]) => ({
                    id,
                    ...value,
                }));
                arr.sort((a, b) => (a.orderId || 0) - (b.orderId || 0));
                setQuestions(arr);
            } else {
                setQuestions([]);
            }
            setLoading(false);
        });
        return () => unsubscribe();
    }, [domainId, categoryId]);

    // Fetch selected interview questions
    useEffect(() => {
        if (!domainId || !categoryId || !packId || !interviewId) return;
        const selectedRef = ref(db, `InterviewQuestions/${domainId}/${packId}/${interviewId}`);
        const unsubscribe = onValue(selectedRef, (snapshot) => {
            const data = snapshot.val();
            setSelectedQuestions(data || {});
        });
        return () => unsubscribe();
    }, [domainId, packId, interviewId]);

    // Toggle question selection
    const toggleQuestion = useCallback(
        async (questionId) => {
            const isSelected = !!selectedQuestions?.[questionId];
            const qRef = ref(db, `InterviewQuestions/${domainId}/${packId}/${interviewId}/${questionId}`);
            if (isSelected) {
                await remove(qRef);
            } else {
                await set(qRef, { selected: true, orderId: 0 });
            }
        },
        [domainId, packId, interviewId, selectedQuestions]
    );

    // Update orderId (with duplicate check)
    const updateOrderId = useCallback(
        async (questionId, newOrderId) => {
            if (!questionId || newOrderId === undefined || newOrderId === "") return;

            const numericId = Number(newOrderId);
            if (isNaN(numericId)) {
                alert("⚠️ Order ID must be a number");
                return;
            }

            // Check for duplicates among selected questions
            const duplicate = Object.entries(selectedQuestions).find(
                ([id, val]) => id !== questionId && val?.orderId === numericId
            );

            if (duplicate) {
                alert(`⚠️ Duplicate order ID "${numericId}" already exists for another question.`);
                return;
            }

            // Update Firebase
            const qRef = ref(db, `InterviewQuestions/${domainId}/${packId}/${interviewId}/${questionId}`);
            await update(qRef, { orderId: numericId });
        },
        [domainId, packId, interviewId, selectedQuestions]
    );

    const selectedIds = new Set(Object.keys(selectedQuestions || {}));

    return {
        questions,
        selectedIds,
        selectedQuestions,
        loading,
        toggleQuestion,
        updateOrderId,
    };
}
